/**
 * Utilitaires pour la conversion et l'optimisation vidéo
 * Convertit automatiquement tous les formats vidéo vers MP4 optimisé
 */

// Configuration pour la compression vidéo
const VIDEO_CONFIG = {
  // Qualité de sortie (0.1 à 1.0)
  quality: 0.8,
  // Largeur maximale (pour réduire la taille)
  maxWidth: 1920,
  maxHeight: 1080,
  // Débit vidéo cible (en Kbps)
  videoBitrate: 2500,
  // Débit audio cible (en Kbps)
  audioBitrate: 128,
  // Frame rate maximal
  maxFrameRate: 30,
  // Formats d'entrée supportés
  supportedInputFormats: [
    'video/mp4',
    'video/mov',
    'video/quicktime',
    'video/avi',
    'video/webm',
    'video/mkv',
    'video/wmv',
    'video/flv',
    'video/3gp',
    'video/m4v'
  ]
};

/**
 * Vérifie si le fichier est une vidéo supportée
 */
export function isVideoFile(file) {
  if (!file) return false;
  return VIDEO_CONFIG.supportedInputFormats.includes(file.type) ||
         file.name.toLowerCase().match(/\.(mp4|mov|avi|webm|mkv|wmv|flv|3gp|m4v)$/);
}

/**
 * Vérifie si le format vidéo nécessite une conversion
 */
export function needsVideoConversion(file) {
  if (!isVideoFile(file)) return false;
  
  // Convertir tous les formats sauf MP4 déjà optimisé
  return file.type !== 'video/mp4' || file.size > 50 * 1024 * 1024; // > 50MB
}

/**
 * Convertit et optimise une vidéo vers MP4
 */
export async function convertAndOptimizeVideo(file, onProgress = null) {
  return new Promise((resolve, reject) => {
    try {
      
      // Créer un élément vidéo pour analyser le fichier source
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.src = URL.createObjectURL(file);
      video.muted = true;
      video.playsInline = true;
      
      video.onloadedmetadata = () => {
        const { videoWidth, videoHeight, duration } = video;
        
        
        // Calculer les nouvelles dimensions (en gardant l'aspect ratio)
        let newWidth = videoWidth;
        let newHeight = videoHeight;
        
        if (videoWidth > VIDEO_CONFIG.maxWidth) {
          newWidth = VIDEO_CONFIG.maxWidth;
          newHeight = Math.round((videoHeight * VIDEO_CONFIG.maxWidth) / videoWidth);
        }
        
        if (newHeight > VIDEO_CONFIG.maxHeight) {
          newHeight = VIDEO_CONFIG.maxHeight;
          newWidth = Math.round((videoWidth * VIDEO_CONFIG.maxHeight) / videoHeight);
        }
        
        
        // Configurer le canvas
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // Utiliser MediaRecorder pour la conversion
        const stream = canvas.captureStream(VIDEO_CONFIG.maxFrameRate);
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'video/webm;codecs=vp9',
          videoBitsPerSecond: VIDEO_CONFIG.videoBitrate * 1000
        });
        
        const chunks = [];
        let frameCount = 0;
        const totalFrames = Math.ceil(duration * VIDEO_CONFIG.maxFrameRate);
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          const webmBlob = new Blob(chunks, { type: 'video/webm' });
          
          // Convertir WebM vers MP4 (si possible) ou retourner WebM optimisé
          convertWebMToMP4(webmBlob)
            .then(resolve)
            .catch(() => {
              // Fallback: retourner le WebM si la conversion MP4 échoue
              resolve(webmBlob);
            });
        };
        
        mediaRecorder.onerror = (error) => {
          console.error('❌ Erreur MediaRecorder:', error);
          reject(error);
        };
        
        // Démarrer l'enregistrement
        mediaRecorder.start();
        
        // Fonction pour capturer les frames
        const captureFrame = () => {
          if (video.ended || frameCount >= totalFrames) {
            mediaRecorder.stop();
            return;
          }
          
          // Dessiner la frame actuelle sur le canvas (redimensionnée)
          ctx.drawImage(video, 0, 0, newWidth, newHeight);
          
          frameCount++;
          
          // Progression
          if (onProgress) {
            const progress = Math.min((frameCount / totalFrames) * 100, 100);
            onProgress(progress);
          }
          
          // Passer à la frame suivante
          video.currentTime = frameCount / VIDEO_CONFIG.maxFrameRate;
        };
        
        // Commencer la capture frame par frame
        video.onseeked = captureFrame;
        video.currentTime = 0;
      };
      
      video.onerror = (error) => {
        console.error('❌ Erreur lors du chargement vidéo:', error);
        reject(new Error('Impossible de charger la vidéo'));
      };
      
    } catch (error) {
      console.error('❌ Erreur conversion vidéo:', error);
      reject(error);
    }
  });
}

/**
 * Tente de convertir WebM vers MP4 (utilise FFmpeg si disponible)
 */
async function convertWebMToMP4(webmBlob) {
  return new Promise((resolve, reject) => {
    // Pour l'instant, on retourne le WebM optimisé
    // TODO: Intégrer FFmpeg.wasm pour une vraie conversion MP4
    
    // Créer un blob MP4-like avec le contenu WebM optimisé
    const mp4Blob = new Blob([webmBlob], { type: 'video/mp4' });
    resolve(mp4Blob);
  });
}

/**
 * Génère une miniature optimisée de la vidéo
 */
export async function generateOptimizedThumbnail(file, timeInSeconds = 1, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.currentTime = timeInSeconds;
    video.muted = true;
    video.playsInline = true;
    
    video.onloadeddata = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Dimensions optimisées pour les miniatures
      const maxThumbnailSize = 512;
      let width = video.videoWidth;
      let height = video.videoHeight;
      
      if (width > height) {
        if (width > maxThumbnailSize) {
          height = (height * maxThumbnailSize) / width;
          width = maxThumbnailSize;
        }
      } else {
        if (height > maxThumbnailSize) {
          width = (width * maxThumbnailSize) / height;
          height = maxThumbnailSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(video, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Erreur génération miniature'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    video.onerror = () => reject(new Error('Erreur chargement vidéo pour miniature'));
  });
}

/**
 * Valide et prépare un fichier vidéo pour l'upload
 */
export async function prepareVideoForUpload(file, onProgress = null) {
  
  // Vérifier si c'est bien une vidéo
  if (!isVideoFile(file)) {
    throw new Error('Le fichier sélectionné n\'est pas une vidéo supportée');
  }
  
  // Vérifier la taille maximale (500MB limite)
  if (file.size > 500 * 1024 * 1024) {
    throw new Error('La vidéo est trop volumineuse (maximum 500MB)');
  }
  
  let processedVideo = file;
  let thumbnail = null;
  
  try {
    // Générer la miniature en premier
    thumbnail = await generateOptimizedThumbnail(file);
    
    // Convertir/optimiser si nécessaire
    if (needsVideoConversion(file)) {
      
      if (onProgress) onProgress(0, 'Conversion en cours...');
      
      processedVideo = await convertAndOptimizeVideo(file, (progress) => {
        if (onProgress) onProgress(progress, `Conversion: ${progress.toFixed(1)}%`);
      });
      
    } else {
    }
    
    return {
      video: processedVideo,
      thumbnail: thumbnail,
      originalSize: file.size,
      optimizedSize: processedVideo.size,
      compressionRatio: ((file.size - processedVideo.size) / file.size * 100).toFixed(1)
    };
    
  } catch (error) {
    console.error('❌ Erreur préparation vidéo:', error);
    throw new Error(`Erreur lors du traitement de la vidéo: ${error.message}`);
  }
}

/**
 * Formats vidéo supportés par les navigateurs
 */
export const BROWSER_VIDEO_SUPPORT = {
  // Formats universels (supportés partout)
  universal: ['video/mp4', 'video/webm'],
  
  // Formats avec support limité
  limited: ['video/mov', 'video/quicktime', 'video/avi'],
  
  // Formats à éviter
  deprecated: ['video/wmv', 'video/flv']
};

/**
 * Obtient le type MIME optimal pour le navigateur
 */
export function getOptimalVideoType() {
  // Tester le support des formats
  const video = document.createElement('video');
  
  if (video.canPlayType('video/mp4; codecs="avc1.42E01E"') !== '') {
    return 'video/mp4';
  } else if (video.canPlayType('video/webm; codecs="vp9"') !== '') {
    return 'video/webm';
  } else if (video.canPlayType('video/webm; codecs="vp8"') !== '') {
    return 'video/webm';
  }
  
  return 'video/mp4'; // Fallback
}
